import { useEffect } from "react";

import { CloseIcon } from "@/assets/icons";
import type { ModuleIntroConfig } from "@/components/onboarding/configs/getModuleConfig";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";

const getButtonVariant = (variant?: "primary" | "secondary") =>
  variant === "secondary" ? "outline" : "default";

type OnboardingIntroDialogProps = {
  intro: ModuleIntroConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStart: () => void;
};

export const OnboardingIntroDialog = ({
  intro,
  open,
  onOpenChange,
  onStart
}: OnboardingIntroDialogProps) => {
  // Stage the UI behind the intro (e.g. open the Dataset Explorer) the moment
  // the dialog opens, and tear it down when the intro closes. Mirrors the
  // per-step onActivate lifecycle in useTourStep.
  useEffect(() => {
    if (!open) return;

    const teardown = intro.onActivate?.();

    return () => {
      teardown?.();
    };
  }, [open, intro]);

  const handleAction = (actionType: "close" | "start") => {
    if (actionType === "close") {
      onOpenChange(false);
      return;
    }

    onStart();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        isHideClose
        zIndex={1005}
        // Dim backdrop above the Dataset Explorer staged by intro.onActivate
        // (z-index 999) so the explorer stays visible but recedes behind the
        // intro. zIndex 1005 keeps this overlay on top of the explorer.
        overlayClassName="bg-black/60"
        className="w-[calc(100vw-2rem)] max-w-[455px] max-h-[calc(100dvh-2rem)] grid-rows-[minmax(0,1fr)] gap-0 rounded-lg border-0 p-0 shadow-[0_24px_80px_rgba(15,23,42,0.28)]"
      >
        <div className="relative flex min-h-0 flex-col overflow-y-auto p-6">
          <div className="flex w-full shrink-0 flex-row items-center justify-between gap-4">
            <DialogTitle className="flex-1 text-xl font-semibold leading-9 text-txtHeading">
              {intro.title}
            </DialogTitle>

            <DialogClose asChild>
              <button
                type="button"
                aria-label={
                  intro.closeButtonAriaLabel ?? `Close ${intro.title} intro`
                }
                className="flex h-6 w-6 items-center justify-center rounded-full text-dim-gray hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
              >
                <CloseIcon className="h-6 w-6 fill-current" />
              </button>
            </DialogClose>
          </div>

          {intro.image && (
            <img
              src={intro.image.src}
              alt={intro.image.alt}
              className="w-full shrink-0 rounded-2xl my-6 h-[200px] object-cover shadow-[0_4px_24px_5px_rgba(206,206,206,0.40)]"
            />
          )}

          <DialogDescription className="max-w-[630px] text-sm text-txtHeading">
            {intro.description}
          </DialogDescription>

          {intro.actions && intro.actions.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {intro.actions.map((action) => (
                <Button
                  key={`${action.type}-${action.label}`}
                  type="button"
                  variant={getButtonVariant(action.variant)}
                  onClick={() => handleAction(action.type)}
                  className="min-w-[126px] rounded-2xl py-2 text-sm font-bold"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingIntroDialog;
